import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api.js';
import { connectToGame } from '../websocket.js';
import { useAuth } from '../context/AuthContext.jsx';
import { usePremium } from '../context/PremiumContext.jsx';
import FmCard from '../components/FmCard.jsx';
import PremiumCrown from '../components/PremiumCrown.jsx';
import InviteModal from '../components/InviteModal.jsx';
import {
  getNonAlcoholicVersion,
  DRINK_SWAP_OPTIONS,
} from '../data/drinks.js';
import {
  YELLOW_CARD,
  RED_CARD,
  CARDS_BY_TYPE,
  newCardRecord,
  normalizeCardRecord,
  issueYellow,
  issueRed,
  computePenaltyTotal,
  totalRedCards,
  visibleYellowCards,
} from '../data/penalties.js';

function normalizeTeams(game) {
  // Accept either `teams` with players, or a flat `players` list.
  if (Array.isArray(game?.teams) && game.teams.length) return game.teams;
  if (Array.isArray(game?.players)) {
    return [
      {
        id: 't1',
        name: 'Players',
        players: game.players.map((p, i) => ({
          id: p.id || `p${i}`,
          name: typeof p === 'string' ? p : p.name,
          scores: [],
          cards: newCardRecord(),
          dd: false,
        })),
      },
    ];
  }
  return [];
}

/**
 * Normalize a hole object so the Scorecard renders consistently for both
 * AI-generated courses (which use Google Places fields like `bar`,
 * `barImage`, `barAddress`) and hand-built custom courses (which use
 * `name`, `photoUrl`, `address`).
 */
function normalizeHole(h, { masked = false } = {}) {
  if (!h) return {};
  const photo =
    h.photoUrl ||
    h.photo ||
    h.imageUrl ||
    h.image ||
    h.thumbnail ||
    h.barImage || // AI course primary photo from Google Places
    (Array.isArray(h.barImages) && h.barImages[0]) ||
    null;
  const base = {
    ...h,
    name: h.name || h.bar || 'Venue',
    address: h.address || h.barAddress || null,
    rating: h.rating || h.barRating || null,
    type: h.type || h.barType || null,
    photo,
    photos: Array.isArray(h.barImages) ? h.barImages : photo ? [photo] : [],
    drinkEmoji: h.emoji || h.drinkEmoji || '🍸',
    par: h.par || 4,
    masked: false,
  };
  if (!masked) return base;
  // Mystery Mode redaction — everything hidden until the hole becomes
  // current. We keep a masked flag so the venue card can render a
  // "???" placeholder state.
  return {
    ...base,
    name: '???',
    address: null,
    rating: null,
    type: null,
    photo: null,
    photos: [],
    drink: null,
    drinkEmoji: '❓',
    par: null,
    masked: true,
  };
}

function strokesFor(player, throughHole) {
  const raw = (player.scores || [])
    .slice(0, throughHole + 1)
    .reduce((a, s) => a + (Number(s) || 0), 0);
  // Penalty strokes accrue across the whole round, not per hole.
  return raw + computePenaltyTotal(player.cards);
}

function currentHoleScore(player, idx) {
  return Number(player.scores?.[idx] || 0);
}

export default function Scorecard() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { premium: viewerPremium, unlimited: viewerUnlimited } = usePremium();
  const viewerTier = viewerUnlimited
    ? 'unlimited'
    : viewerPremium
      ? 'premium'
      : 'free';

  // The viewer's claimed player name in this round (set on the
  // ClaimPlayer screen and persisted in localStorage). Used to attach a
  // premium crown to their row and to detect caddy permissions.
  const claimedPlayerName = (() => {
    try {
      const raw = localStorage.getItem(`pubgolf.player.${code}`);
      return raw ? JSON.parse(raw)?.name : null;
    } catch {
      return null;
    }
  })();

  // Is this browser the HOST that created the round? The host gets
  // caddy controls without having to claim a player slot.
  const isHost = (() => {
    try {
      const raw = localStorage.getItem(`pubgolf.host.${code}`);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      // If we have a userId on the host record and on the auth user,
      // they must match. If host was recorded pre-login we just trust
      // the localStorage flag on this browser.
      if (parsed?.userId && user?.uid) return parsed.userId === user.uid;
      return true;
    } catch {
      return false;
    }
  })();

  const [game, setGame] = useState(null);
  const [teams, setTeams] = useState([]);
  const [hole, setHole] = useState(0);
  const [error, setError] = useState(null);
  const [penaltyFor, setPenaltyFor] = useState(null); // {teamIdx, playerIdx}
  const [caddyPickerOpen, setCaddyPickerOpen] = useState(false);
  const [caddyName, setCaddyName] = useState(null);
  const [mysteryMode, setMysteryMode] = useState(false);
  const [drinkSwapOpen, setDrinkSwapOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [courseOpen, setCourseOpen] = useState(false);
  const [routeOpen, setRouteOpen] = useState(false);
  const wsRef = useRef(null);

  // Initial load — try the server first; fall back to a locally-stored
  // game (custom courses created without a backend round-trip).
  useEffect(() => {
    const hydrate = (game) => {
      const norm = game?.game || game;
      setGame(norm);
      const t = normalizeTeams(norm);
      const holeCount = norm?.holes?.length || norm?.holeCount || 9;
      t.forEach((team) =>
        team.players.forEach((p) => {
          p.scores = Array.from(
            { length: holeCount },
            (_, i) => p.scores?.[i] ?? 0
          );
          p.cards = normalizeCardRecord(p.cards);
        })
      );
      setTeams(t);
      // Mystery Mode — hide upcoming holes until they become current.
      setMysteryMode(
        norm.mysteryMode === true ||
          norm.mystery_mode === 1 ||
          norm.mystery_mode === true
      );
      // Seed the current caddy — either from the server response or default
      // to the first player of the first team.
      const serverCaddy = norm.caddyName || norm.caddy_name;
      if (serverCaddy) {
        setCaddyName(serverCaddy);
      } else if (t[0]?.players?.[0]?.name) {
        setCaddyName(t[0].players[0].name);
      }

      // Auto-show the Invite modal once per session when a fresh game
      // first loads — if nobody has claimed a slot yet AND we haven't
      // already shown it for this code in this tab, pop it open so the
      // host can share the QR code immediately. Only the host sees it —
      // people who joined via code don't need to be prompted to share.
      try {
        const inviteKey = `pubgolf.inviteShown.${code}`;
        const alreadyShown = sessionStorage.getItem(inviteKey) === '1';
        const hostFlag = localStorage.getItem(`pubgolf.host.${code}`);
        const anyClaimed = t.some((tm) =>
          (tm.players || []).some((p) => p.userId)
        );
        const allHolesZero =
          t.every((tm) =>
            (tm.players || []).every(
              (p) => !p.scores || p.scores.every((s) => !Number(s))
            )
          );
        if (!alreadyShown && hostFlag && !anyClaimed && allHolesZero) {
          setInviteOpen(true);
          sessionStorage.setItem(inviteKey, '1');
        }
      } catch {}
    };

    const tryLocal = () => {
      try {
        const raw = sessionStorage.getItem(`pubgolf.localGame.${code}`);
        if (raw) {
          const local = JSON.parse(raw);
          hydrate(local);
          return true;
        }
      } catch {}
      return false;
    };

    // Local-prefixed codes never hit the server.
    if (code?.startsWith('LOCAL-')) {
      if (!tryLocal()) setError('Local game not found in this browser.');
      return;
    }

    api
      .joinGame(code)
      .then((g) => hydrate(g))
      .catch((err) => {
        // Last-resort: if the server says "not found", check sessionStorage.
        if (!tryLocal()) setError(err.message);
      });
  }, [code]);

  // WebSocket sync
  useEffect(() => {
    if (!code) return;
    const conn = connectToGame(code, (msg) => {
      if (msg.type === 'sync' && msg.teams) {
        setTeams(msg.teams);
        if (typeof msg.currentHole === 'number') setHole(msg.currentHole);
        if (typeof msg.caddyName === 'string') setCaddyName(msg.caddyName);
        if (Array.isArray(msg.holes)) {
          setGame((prev) => (prev ? { ...prev, holes: msg.holes } : prev));
        }
      }
    });
    wsRef.current = conn;
    return () => conn.disconnect();
  }, [code]);

  const broadcast = (nextTeams, nextHole = hole) => {
    wsRef.current?.sendUpdate('playing', nextTeams, nextHole);
  };

  const holes = game?.holes || [];
  const holeCount = holes.length || game?.holeCount || 9;
  const currentHole = normalizeHole(holes[hole] || {});
  const par = currentHole.par || 4;

  // ===== Caddy permissions =====
  // The caddy is the ONLY one who can edit scores, issue cards, swap
  // drinks, toggle DD, navigate holes, or finish the round. Everyone
  // else joined via QR/code gets a read-only live view.
  //
  // Detection rules (in order):
  //   1. The current user's claimed player name matches `caddyName`
  //      (after claim flow — real players transferring caddy between
  //      each other).
  //   2. The user is the host of this round (created it locally) AND
  //      either the caddy is unset OR the caddy name doesn't match any
  //      claimed real player yet (no one has taken over).
  //   3. Fallback: the server-side game.host_id matches this user.
  //
  // If none of the above, we render the read-only "following" view.
  const matchesCaddyByName = !!(
    caddyName &&
    claimedPlayerName &&
    caddyName === claimedPlayerName
  );

  // Does some claimed player currently own the caddy name? If so, the
  // host's implicit caddyship has been passed to a real player.
  const caddyOwnedByClaimedPlayer = !!(
    caddyName &&
    teams.some((t) =>
      (t.players || []).some((p) => p.name === caddyName && p.userId)
    )
  );

  const serverHostMatch =
    !!(game?.host_id && user?.uid && game.host_id === user.uid);

  const isCaddy =
    matchesCaddyByName ||
    (isHost && !caddyOwnedByClaimedPlayer) ||
    (serverHostMatch && !caddyOwnedByClaimedPlayer);

  const adjustScore = (teamIdx, playerIdx, delta) => {
    if (!isCaddy) return; // Read-only guard for non-caddy viewers
    setTeams((prev) => {
      const next = prev.map((t) => ({
        ...t,
        players: t.players.map((p) => ({ ...p, scores: [...p.scores] })),
      }));
      const p = next[teamIdx].players[playerIdx];
      p.scores[hole] = Math.max(0, (Number(p.scores[hole]) || 0) + delta);
      broadcast(next);
      return next;
    });
  };

  /**
   * Issue a card. `kind` is 'yellow' | 'red'. The Two-Yellows rule is
   * applied inside the issueYellow helper.
   */
  const issuePenalty = (teamIdx, playerIdx, kind, reason) => {
    if (!isCaddy) return null; // Read-only guard
    let result = null;
    setTeams((prev) => {
      const next = prev.map((t, ti) =>
        ti === teamIdx
          ? {
              ...t,
              players: t.players.map((p, pi) => {
                if (pi !== playerIdx) return p;
                const cards =
                  kind === 'yellow'
                    ? issueYellow(p.cards, { reason, hole })
                    : issueRed(p.cards, { reason, hole });
                result = cards;
                return { ...p, cards };
              }),
            }
          : t
      );
      broadcast(next);
      return next;
    });
    return result;
  };

  const toggleDD = (teamIdx, playerIdx) => {
    if (!isCaddy) return; // Read-only guard
    setTeams((prev) => {
      const next = prev.map((t) => ({
        ...t,
        players: t.players.map((p) => ({ ...p })),
      }));
      next[teamIdx].players[playerIdx].dd =
        !next[teamIdx].players[playerIdx].dd;
      broadcast(next);
      return next;
    });
  };

  /**
   * Swap the current hole's drink at the hole level. Persists into the
   * game's holes array and broadcasts via WebSocket so everyone in the
   * round sees the same swap.
   */
  const swapDrinkAtCurrentHole = (newDrink) => {
    if (!isCaddy) return; // Read-only guard
    if (!game) return;
    setGame((prev) => {
      const next = {
        ...prev,
        holes: [...(prev.holes || [])],
      };
      next.holes[hole] = {
        ...next.holes[hole],
        drink: newDrink,
        drinkEmoji: next.holes[hole]?.drinkEmoji || '🍸',
        swapped: true,
      };
      // Broadcast the updated holes too.
      wsRef.current?.sendUpdate('playing', teams, hole, { holes: next.holes });
      return next;
    });
    setDrinkSwapOpen(false);
  };

  const goHole = (idx) => {
    if (!isCaddy) return; // Only the caddy can advance holes
    if (idx < 0 || idx >= holeCount) return;
    setHole(idx);
    broadcast(teams, idx);
  };

  const finishGame = async () => {
    if (!isCaddy) return; // Only the caddy can finish the round
    // Compute final team totals so the server can store the winner.
    try {
      const teamTotals = teams.map((t) => ({
        name: t.name,
        total: (t.players || []).reduce((a, p) => {
          const raw = (p.scores || []).reduce(
            (x, s) => x + (Number(s) || 0),
            0
          );
          return a + raw + computePenaltyTotal(p.cards);
        }, 0),
      }));
      const winner = teamTotals.sort((a, b) => a.total - b.total)[0];
      await api.finishGame(code, {
        teams,
        userId: user?.uid,
        winnerTeam: winner?.name || null,
        winnerScore: winner?.total ?? null,
      });
    } catch {}
    navigate(`/game/${code}/results`);
  };

  if (error) {
    return (
      <div className="screen">
        <div className="empty-state">
          <div className="icon">⚠️</div>
          <h3>Couldn't load game</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/')}>
            Home
          </button>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="screen center">
        <div className="spinner" />
      </div>
    );
  }

  const openRoute = () => {
    // If Mapbox token is configured, open the in-app route modal.
    // Otherwise fall back to Google Maps directions in a new tab.
    if (import.meta.env.VITE_MAPBOX_TOKEN) {
      setRouteOpen(true);
      return;
    }
    const stops = (holes || [])
      .map((h) => normalizeHole(h).name || '')
      .filter(Boolean)
      .map(encodeURIComponent)
      .join('/');
    window.open(
      `https://www.google.com/maps/dir/${stops}`,
      '_blank'
    );
  };

  return (
    <div className="screen" style={{ paddingBottom: '8rem' }}>
      <div className="live-header">
        <button className="back-btn" onClick={() => navigate('/')}>←</button>
        <div className="live-badge">
          <span className="dot" /> {mysteryMode ? '🎭 MYSTERY' : 'LIVE'}
        </div>
        <button
          className="code-pill code-pill-btn"
          onClick={() => setInviteOpen(true)}
          title="Invite players"
        >
          {code} <span style={{ marginLeft: 4, opacity: 0.55 }}>⇪</span>
        </button>
      </div>

      {!isCaddy && (
        <div className="following-banner">
          <span className="following-banner-icon">👀</span>
          <span>
            You're following this round.{' '}
            <strong>{caddyName || 'The caddy'}</strong> is scoring.
          </span>
        </div>
      )}

      <div className="progress-pips" style={{ margin: '0.75rem 0 1rem' }}>
        {Array.from({ length: holeCount }).map((_, i) => (
          <div
            key={i}
            className={
              'progress-pip ' +
              (i < hole ? 'done' : i === hole ? 'current' : '')
            }
          />
        ))}
      </div>

      {holes[hole + 1] && (
        <div className="next-bar">
          <span>🚶</span>
          <span className="label">
            Next: {normalizeHole(holes[hole + 1], { masked: mysteryMode }).name} · {mysteryMode ? 'Mystery' : '0.1 mi · ~2 min'}
          </span>
          <span className="arrow">›</span>
        </div>
      )}

      <div className="scorecard-body">
      <div className="venue-card">
        <VenueHero hole={currentHole} holeNumber={hole + 1} />
        <div className="info">
          <div className="par-circle">
            <span className="num">{par}</span>
            <span className="label">PAR</span>
          </div>
          <div className="hole-label">HOLE {hole + 1}</div>
          <h2>{currentHole.name}</h2>
          {currentHole.address && (
            <div className="venue-address">📍 {currentHole.address}</div>
          )}
          <div className="drink">
            <span>{currentHole.drinkEmoji}</span>
            <span>{currentHole.drink || 'House special'}</span>
          </div>
          {currentHole.rating && (
            <div className="rating">
              <span>★</span>
              <span>{Number(currentHole.rating).toFixed(1)}</span>
              {currentHole.type && (
                <span className="text-muted" style={{ marginLeft: 6 }}>
                  · {currentHole.type}
                </span>
              )}
            </div>
          )}
          <div className="chips-row">
            {(currentHole.waterHazard || currentHole.isWaterHazard) && (
              <span className="chip chip-hazard">⚠️ HAZARD</span>
            )}
            <button className="chip" onClick={() => setCourseOpen(true)}>
              COURSE
            </button>
            {isCaddy && (
              <button
                className={
                  'chip' +
                  (currentHole.swapped ? ' chip-accent' : '')
                }
                onClick={() => setDrinkSwapOpen(true)}
                disabled={currentHole.masked}
              >
                🔀 SWAP
              </button>
            )}
            <button
              className="chip chip-accent"
              onClick={() => isCaddy && setCaddyPickerOpen(true)}
              disabled={!isCaddy}
              title={
                isCaddy
                  ? 'Transfer caddy'
                  : `Current caddy: ${caddyName || '—'}`
              }
            >
              🎖 {caddyName ? caddyName : 'CADDY'} {isCaddy ? '›' : ''}
            </button>
            <button className="chip" onClick={openRoute}>ROUTE</button>
          </div>
        </div>
      </div>

      <div className="scorecard-teams">
      {teams.map((team, tIdx) => {
        // Cumulative through current hole + accumulated penalty strokes.
        const totalStrokes = team.players.reduce(
          (a, p) => a + strokesFor(p, hole),
          0
        );
        const parThrough =
          holes.slice(0, hole + 1).reduce((a, h) => a + (h.par || 4), 0) *
          team.players.length;
        const diff = totalStrokes - parThrough;

        return (
          <div className="team-card" key={team.id || tIdx}>
            <div className="header">
              <div className="name">
                <span className="dot" /> {team.name}
              </div>
              <div className="total">
                <div className="num">{totalStrokes}</div>
                {diff !== 0 && (
                  <div className="diff">
                    {diff > 0 ? `+${diff}` : diff}
                  </div>
                )}
              </div>
            </div>
            {(!team.players || team.players.length === 0) && (
              <div className="player-row-empty">
                <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                  Waiting for players to join…
                </div>
              </div>
            )}
            {(team.players || []).map((p, pIdx) => {
              const visYellows = visibleYellowCards(p.cards);
              const reds = totalRedCards(p.cards);
              const penalty = computePenaltyTotal(p.cards);
              // Match "You" by userId first (reliable across name changes),
              // then fall back to the localStorage claimed name for guests.
              const isYou =
                (p.userId && user?.uid && p.userId === user.uid) ||
                (!p.userId && claimedPlayerName === p.name);
              return (
                <div
                  className={'player-row' + (isYou ? ' player-row-you' : '')}
                  key={p.id || pIdx}
                >
                  <div className="avatar">
                    {p.photoURL || p.photoUrl ? (
                      <img
                        src={p.photoURL || p.photoUrl}
                        alt={p.name}
                        referrerPolicy="no-referrer"
                      />
                    ) : p.avatar &&
                      // treat short strings as emoji avatars
                      [...String(p.avatar)].length <= 2 ? (
                      <span className="avatar-emoji">{p.avatar}</span>
                    ) : (
                      p.name?.[0]?.toUpperCase() || '?'
                    )}
                  </div>
                  <div className="info">
                    <div className="name-with-cards">
                      <span className="name">
                        {p.name}
                        {p.name === caddyName && (
                          <span className="caddy-badge" title="Caddy — scoring this round">
                            🏌️ Caddy
                          </span>
                        )}
                        {isYou && (
                          <span className="you-badge" title="That's you">
                            ✓ You
                          </span>
                        )}
                        {(p.tier === 'premium' ||
                          p.tier === 'unlimited' ||
                          (claimedPlayerName === p.name &&
                            (viewerPremium || viewerUnlimited))) && (
                          <PremiumCrown
                            tier={
                              p.tier ||
                              (viewerUnlimited ? 'unlimited' : 'premium')
                            }
                          />
                        )}
                      </span>
                      {visYellows > 0 && (
                        <span className="card-badge">
                          {Array.from({ length: Math.min(visYellows, 2) }).map((_, i) => (
                            <FmCard key={i} type="yellow" size="sm" />
                          ))}
                          {visYellows > 2 && <em>×{visYellows}</em>}
                        </span>
                      )}
                      {reds > 0 && (
                        <span className="card-badge">
                          {Array.from({ length: Math.min(reds, 2) }).map((_, i) => (
                            <FmCard key={i} type="red" size="sm" />
                          ))}
                          {reds > 2 && <em>×{reds}</em>}
                        </span>
                      )}
                    </div>
                    <div className="sub">
                      {currentHoleScore(p, hole) || 0} this hole
                      {penalty > 0 && ` · +${penalty} pen`}
                      {p.dd && (
                        <>
                          {' · '}
                          <span className="dd-badge">🚗 DD</span>
                          {currentHole.drink && !currentHole.masked && (
                            <span className="dd-alt">
                              {' '}
                              → {getNonAlcoholicVersion(currentHole.drink)}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className={'score-stepper' + (isCaddy ? '' : ' score-stepper-readonly')}>
                    <button
                      onClick={() => adjustScore(tIdx, pIdx, -1)}
                      disabled={!isCaddy}
                      aria-hidden={!isCaddy}
                      tabIndex={isCaddy ? 0 : -1}
                    >
                      −
                    </button>
                    <div className="value">{currentHoleScore(p, hole)}</div>
                    <button
                      className="plus"
                      onClick={() => adjustScore(tIdx, pIdx, +1)}
                      disabled={!isCaddy}
                      aria-hidden={!isCaddy}
                      tabIndex={isCaddy ? 0 : -1}
                    >
                      +
                    </button>
                  </div>
                  {isCaddy && (
                    <button
                      className="penalty-card-btn"
                      onClick={() => setPenaltyFor({ teamIdx: tIdx, playerIdx: pIdx })}
                      aria-label="Issue a card"
                    >
                      <FmCard type="yellow-red" size="sm" className={penalty === 0 ? 'penalty-card-btn-empty' : ''} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
      </div>
      </div>

      {isCaddy ? (
        <div className="scorecard-footer">
          {hole > 0 ? (
            <button
              className="btn btn-ghost"
              onClick={() => goHole(hole - 1)}
            >
              ‹ Hole {hole}
            </button>
          ) : (
            <div className="scorecard-footer-spacer" />
          )}
          {hole < holeCount - 1 ? (
            <button className="btn btn-primary" onClick={() => goHole(hole + 1)}>
              Hole {hole + 2} ›
            </button>
          ) : (
            <button className="btn btn-primary" onClick={finishGame}>
              Finish Round
            </button>
          )}
        </div>
      ) : (
        <div className="scorecard-footer scorecard-footer-readonly">
          <div className="following-hint">
            Hole {hole + 1} of {holeCount} · Scores update live
          </div>
        </div>
      )}

      {penaltyFor && (
        <PenaltyModal
          player={teams[penaltyFor.teamIdx]?.players[penaltyFor.playerIdx]}
          onClose={() => setPenaltyFor(null)}
          onIssue={(kind, reason) => {
            const cards = issuePenalty(penaltyFor.teamIdx, penaltyFor.playerIdx, kind, reason);
            return cards;
          }}
          onToggleDD={() => toggleDD(penaltyFor.teamIdx, penaltyFor.playerIdx)}
        />
      )}

      {caddyPickerOpen && (
        <CaddyPicker
          teams={teams}
          currentCaddy={caddyName}
          onClose={() => setCaddyPickerOpen(false)}
          onTransfer={async (newName) => {
            setCaddyName(newName);
            setCaddyPickerOpen(false);
            // Fire-and-forget: tell the server so other clients see it.
            if (!code?.startsWith('LOCAL-')) {
              try {
                await api.transferCaddy(code, caddyName || '', newName);
              } catch (err) {
                console.warn('transferCaddy failed', err);
              }
            }
            // Broadcast via WebSocket too for instant sync.
            wsRef.current?.sendUpdate('playing', teams, hole, { caddyName: newName });
          }}
        />
      )}

      {drinkSwapOpen && (
        <DrinkSwapPicker
          currentDrink={currentHole.drink}
          onClose={() => setDrinkSwapOpen(false)}
          onSwap={swapDrinkAtCurrentHole}
        />
      )}

      {inviteOpen && (
        <InviteModal code={code} onClose={() => setInviteOpen(false)} />
      )}

      {courseOpen && (
        <CourseModal
          holes={holes}
          currentHoleIdx={hole}
          masked={mysteryMode}
          onClose={() => setCourseOpen(false)}
          onJump={(idx) => {
            if (isCaddy) goHole(idx);
            setCourseOpen(false);
          }}
        />
      )}

      {routeOpen && (
        <RouteModal
          holes={holes}
          currentHoleIdx={hole}
          onClose={() => setRouteOpen(false)}
        />
      )}
    </div>
  );
}

// ===== Drink swap modal =====
function DrinkSwapPicker({ currentDrink, onClose, onSwap }) {
  const [custom, setCustom] = useState('');
  const submitCustom = (e) => {
    e.preventDefault();
    const v = custom.trim();
    if (v) onSwap(v);
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="penalty-modal-header">
          <h2>🔀 Swap Drink</h2>
          <span className="text-muted">
            Currently: <strong>{currentDrink || 'House special'}</strong>
          </span>
        </div>

        <div className="reason-list">
          {DRINK_SWAP_OPTIONS.map((opt) => {
            const active =
              opt.label.toLowerCase() === String(currentDrink || '').toLowerCase();
            return (
              <button
                key={opt.label}
                className={'reason-row reason-yellow'}
                onClick={() => !active && onSwap(opt.label)}
                disabled={active}
              >
                <span className="reason-text">
                  <span style={{ fontSize: '1.1rem', marginRight: '0.5rem' }}>
                    {opt.emoji}
                  </span>
                  {opt.label}
                </span>
                {active && (
                  <span className="reason-strokes" style={{ color: 'var(--accent)' }}>
                    ● Current
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <form onSubmit={submitCustom} className="drink-swap-custom">
          <input
            className="setup-input"
            value={custom}
            placeholder="Or type a custom drink…"
            onChange={(e) => setCustom(e.target.value)}
          />
          <button
            className="btn btn-primary"
            type="submit"
            disabled={!custom.trim()}
          >
            Swap
          </button>
        </form>
      </div>
    </div>
  );
}

// ===== Caddy picker modal =====
// ===== Venue hero photo =====
function VenueHero({ hole, holeNumber }) {
  const [imgFailed, setImgFailed] = useState(false);
  const photo = !imgFailed && hole.photo ? hole.photo : null;

  if (photo) {
    return (
      <div className="venue-hero venue-hero-photo">
        <img
          src={photo}
          alt={hole.name}
          onError={() => setImgFailed(true)}
          loading="lazy"
        />
        <div className="venue-hero-shade" />
        <span className="hole-pill">HOLE {holeNumber}</span>
        <div className="venue-hero-caption">
          <div className="venue-hero-name">{hole.name}</div>
          {hole.type && (
            <div className="venue-hero-type">{hole.type}</div>
          )}
        </div>
      </div>
    );
  }

  // No photo — render a branded placeholder with a giant initial.
  const initial = (hole.name?.[0] || '?').toUpperCase();
  return (
    <div className="venue-hero venue-hero-placeholder">
      <span className="hole-pill">HOLE {holeNumber}</span>
      <div className="venue-hero-initial">{initial}</div>
      <div className="venue-hero-caption">
        <div className="venue-hero-name">{hole.name}</div>
      </div>
    </div>
  );
}

function CaddyPicker({ teams, currentCaddy, onClose, onTransfer }) {
  const allPlayers = teams.flatMap((t) =>
    (t.players || []).map((p) => ({ name: p.name, team: t.name }))
  );
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="penalty-modal-header">
          <h2>🎖 Transfer Caddy Role</h2>
          <span className="text-muted">
            The caddy records scores and issues penalties.
          </span>
        </div>
        <div className="reason-list">
          {allPlayers.map((p, i) => {
            const isCurrent = p.name === currentCaddy;
            return (
              <button
                key={`${p.name}-${i}`}
                className={`reason-row ${isCurrent ? 'reason-current' : 'reason-yellow'}`}
                onClick={() => !isCurrent && onTransfer(p.name)}
                disabled={isCurrent}
              >
                <span className="reason-text">
                  <strong>{p.name}</strong>
                  <span className="text-muted" style={{ fontSize: '0.75rem', display: 'block' }}>
                    {p.team}
                  </span>
                </span>
                {isCurrent ? (
                  <span className="reason-strokes" style={{ color: 'var(--accent)' }}>
                    ● Current
                  </span>
                ) : (
                  <span className="reason-strokes">Transfer →</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ===== Multi-step penalty modal =====

function PenaltyModal({ player, onClose, onIssue, onToggleDD }) {
  // stages: 'choose-card' | 'choose-reason' | 'confirm'
  const [stage, setStage] = useState('choose-card');
  const [pickedCard, setPickedCard] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  if (!player) return null;

  const issue = (reason) => {
    const cards = onIssue(pickedCard.type, reason);
    const triggeredAutoRed =
      pickedCard.type === 'yellow' &&
      cards?.cardLog?.[cards.cardLog.length - 1]?.autoRed;
    setConfirmation({
      type: pickedCard.type,
      reason,
      autoRed: triggeredAutoRed,
    });
    setStage('confirm');
    setTimeout(() => {
      onClose();
    }, 1500);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-sheet penalty-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {stage === 'choose-card' && (
          <>
            <div className="penalty-modal-header">
              <h2>Issue a Card</h2>
              <span className="text-muted">
                Caddy's call · {player.name}
              </span>
            </div>

            {[YELLOW_CARD, RED_CARD].map((card) => (
              <button
                key={card.type}
                className={`penalty-modal-card penalty-card-${card.type}`}
                onClick={() => {
                  setPickedCard(card);
                  setStage('choose-reason');
                }}
              >
                <FmCard type={card.type} size="xl" />
                <div className="penalty-modal-body">
                  <div className="penalty-modal-title">
                    <strong>{card.label}</strong>
                    <span>{card.description} each</span>
                  </div>
                  <div className="penalty-modal-blurb">{card.blurb}</div>
                </div>
                <span className="penalty-modal-chev">›</span>
              </button>
            ))}

            <button
              className="btn btn-ghost btn-block"
              style={{ marginTop: '0.75rem' }}
              onClick={onToggleDD}
            >
              {player.dd ? '🚗 Clear DD Mode' : '🚗 Toggle DD Mode'}
            </button>
          </>
        )}

        {stage === 'choose-reason' && pickedCard && (
          <>
            <div className="penalty-modal-header">
              <button
                className="modal-back"
                onClick={() => setStage('choose-card')}
                aria-label="Back"
              >
                ←
              </button>
              <div className="penalty-modal-header-title">
                <FmCard type={pickedCard.type} size="lg" />
                <div>
                  <h2>{pickedCard.label}</h2>
                  <span className="text-muted">{player.name} · Pick the infraction</span>
                </div>
              </div>
            </div>

            <div className="reason-list">
              {pickedCard.infractions.map((reason) => (
                <button
                  key={reason}
                  className={`reason-row reason-${pickedCard.type}`}
                  onClick={() => issue(reason)}
                >
                  <span className="reason-text">{reason}</span>
                  <span className="reason-strokes">{pickedCard.description}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {stage === 'confirm' && confirmation && (
          <div className="penalty-confirm">
            {confirmation.autoRed ? (
              <FmCard type="yellow-red" size="xl" />
            ) : (
              <FmCard type={confirmation.type} size="xl" />
            )}
            <div className="penalty-confirm-title">
              {confirmation.autoRed ? 'Two Yellows!' : `${CARDS_BY_TYPE[confirmation.type].label} issued`}
            </div>
            <div className="penalty-confirm-sub">
              {confirmation.autoRed
                ? `${player.name}'s 2nd yellow becomes a red — +2 strokes`
                : `${player.name} · ${confirmation.reason}`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ===== Course overview modal =====
// Lists every hole in the round with bar name, drink, par and the
// walking distance to the next stop. The current hole is highlighted
// and (for the caddy) tapping a hole jumps to it.
function CourseModal({ holes, currentHoleIdx, masked, onClose, onJump }) {
  const rows = (holes || []).map((h, i) => {
    const nh = normalizeHole(h, { masked: masked && i > currentHoleIdx });
    return { idx: i, hole: nh, raw: h };
  });
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-sheet course-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="penalty-modal-header">
          <h2>⛳ Course Overview</h2>
          <span className="text-muted">{rows.length} holes · tap to jump</span>
        </div>
        <div className="course-list">
          {rows.map(({ idx, hole }) => {
            const isCurrent = idx === currentHoleIdx;
            const isDone = idx < currentHoleIdx;
            return (
              <button
                key={idx}
                className={
                  'course-row' +
                  (isCurrent ? ' course-row-current' : '') +
                  (isDone ? ' course-row-done' : '')
                }
                onClick={() => onJump(idx)}
              >
                <div className="course-row-num">{idx + 1}</div>
                <div className="course-row-body">
                  <div className="course-row-name">{hole.name}</div>
                  <div className="course-row-sub">
                    <span>{hole.drinkEmoji}</span>
                    <span>{hole.drink || 'House special'}</span>
                    <span className="text-muted"> · Par {hole.par || 4}</span>
                    {(hole.waterHazard || hole.isWaterHazard) && (
                      <span className="chip chip-hazard" style={{ marginLeft: 6 }}>
                        ⚠️ HAZARD
                      </span>
                    )}
                  </div>
                </div>
                <div className="course-row-chev">
                  {isCurrent ? '●' : isDone ? '✓' : '›'}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ===== In-app route map modal =====
// Renders a Mapbox map with a numbered marker for every hole and a
// simple polyline connecting them in order. The current hole marker is
// highlighted. Mapbox GL is loaded lazily so the initial bundle stays
// small. Coordinates are pulled from each hole's lat/lng fields — holes
// missing coordinates are skipped. If no holes have coordinates, we
// fall back to an address-based Google Maps directions link.
function RouteModal({ holes, currentHoleIdx, onClose }) {
  const containerRef = useRef(null);
  const [err, setErr] = useState(null);

  // Normalize & extract coordinates from all possible field names.
  const points = useMemo(() => {
    return (holes || [])
      .map((h, i) => {
        const lat = Number(h?.lat ?? h?.latitude ?? h?.barLat);
        const lng = Number(h?.lng ?? h?.lon ?? h?.longitude ?? h?.barLng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return {
          idx: i,
          lat,
          lng,
          name: normalizeHole(h).name,
        };
      })
      .filter(Boolean);
  }, [holes]);

  useEffect(() => {
    let map;
    let cancelled = false;
    (async () => {
      const token = import.meta.env.VITE_MAPBOX_TOKEN;
      if (!token) {
        setErr('Mapbox token missing');
        return;
      }
      if (points.length === 0) {
        setErr('No coordinates available for this course');
        return;
      }
      try {
        const mod = await import('mapbox-gl');
        await import('mapbox-gl/dist/mapbox-gl.css');
        const mapboxgl = mod.default || mod;
        mapboxgl.accessToken = token;
        if (cancelled || !containerRef.current) return;

        map = new mapboxgl.Map({
          container: containerRef.current,
          style: 'mapbox://styles/mapbox/streets-v12',
          projection: 'mercator',
          dragRotate: false,
          pitchWithRotate: false,
          center: [points[0].lng, points[0].lat],
          zoom: 13,
        });

        map.on('load', () => {
          // Walking route line
          map.addSource('route', {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: points.map((p) => [p.lng, p.lat]),
              },
            },
          });
          map.addLayer({
            id: 'route-line',
            type: 'line',
            source: 'route',
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: {
              'line-color': '#1f5f3f',
              'line-width': 4,
              'line-dasharray': [0.5, 1.5],
            },
          });

          // Numbered markers
          points.forEach((p) => {
            const el = document.createElement('div');
            el.className =
              'route-marker' +
              (p.idx === currentHoleIdx ? ' route-marker-current' : '') +
              (p.idx < currentHoleIdx ? ' route-marker-done' : '');
            el.textContent = String(p.idx + 1);
            new mapboxgl.Marker({ element: el, anchor: 'center' })
              .setLngLat([p.lng, p.lat])
              .setPopup(new mapboxgl.Popup({ offset: 16 }).setText(p.name))
              .addTo(map);
          });

          // Fit bounds to show all stops
          const bounds = new mapboxgl.LngLatBounds();
          points.forEach((p) => bounds.extend([p.lng, p.lat]));
          map.fitBounds(bounds, { padding: 60, maxZoom: 15 });
        });
      } catch (e) {
        if (!cancelled) setErr(e.message || 'Failed to load map');
      }
    })();
    return () => {
      cancelled = true;
      if (map) map.remove();
    };
  }, [points, currentHoleIdx]);

  const openGoogleFallback = () => {
    const stops = (holes || [])
      .map((h) => normalizeHole(h).name || '')
      .filter(Boolean)
      .map(encodeURIComponent)
      .join('/');
    window.open(`https://www.google.com/maps/dir/${stops}`, '_blank');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-sheet route-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="penalty-modal-header">
          <h2>🗺 Route</h2>
          <span className="text-muted">
            {points.length} stops · current hole highlighted
          </span>
        </div>
        {err ? (
          <div className="route-fallback">
            <p className="text-muted" style={{ marginBottom: '1rem' }}>
              {err === 'Mapbox token missing'
                ? 'Map unavailable — opening Google Maps instead.'
                : err}
            </p>
            <button className="btn btn-primary btn-block" onClick={openGoogleFallback}>
              Open in Google Maps
            </button>
          </div>
        ) : (
          <div ref={containerRef} className="route-map-container" />
        )}
      </div>
    </div>
  );
}
